import { LocationMarkerIcon } from "@heroicons/react/outline";
import {
  capitalize,
  every,
  find,
  get,
  head,
  isEmpty,
  keys,
  last,
  map,
  orderBy,
  reduce,
  toLower,
  values,
  words
} from "lodash";
import moment from "moment";
import { stringifyUrl } from "query-string";
import Tooltip from "rc-tooltip";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import AlertMessage from "../../../components/AlertMessage";
import CenteredLoading from "../../../components/CenteredLoading";
import ExternalLink from "../../../components/ExternalLink";
import FeatureExplanation from "../../../components/FeatureExplanation";
import InfoIcon from "../../../components/icons/InfoIcon";
import LoadingIcon from "../../../components/icons/LoadingIcon";
import FilterLabels from "../../../components/labels/FilterLabels";
import Labels from "../../../components/labels/Labels";
import { getLabelColorStyle } from "../../../components/labels/utils";
import LocationActionsButton from "../../../components/LocationActionsButton";
import UpgradeSimulationsModal from "../../../components/modals/UpgradeSimulationsModal";
import NotificationBar from "../../../components/NotificationBar";
import { useAppearance } from "../../../context/appearance";
import { useAuthentication } from "../../../context/authentication";
import { useBillingPlans } from "../../../context/billingPlans/BillingPlansProvider";
import { DottedMenu } from "../../../context/dropdownMenu/DropdownMenuProvider";
import { getSmartLabelFromKey } from "../../../context/filteredList/FilteredListProvider";
import { useHotspots } from "../../../context/hotspots";
import { useInstalls } from "../../../context/installs";
import { useLabels } from "../../../context/labels";
import { useLoading } from "../../../context/loading";
import { LoadingKey } from "../../../context/loading/LoadingProvider";
import { useLocations } from "../../../context/locations";
import { useMembers } from "../../../context/members/MembersProvider";
import { useStats } from "../../../context/stats";
import { renderPercentileClassification } from "../../../context/stats/StatsProvider";
import { useToast } from "../../../context/toast";
import { ModalConfig } from "../../../context/toast/ToastProvider";
import { useTransmitScales } from "../../../context/transmitScales";
import { useWorkspaces } from "../../../context/workspaces";
import classNames from "../../../utils/classNames";
import { avatarsApi, Entity } from "../../../utils/constants";
import { downloadCsv, downloadFile } from "../../../utils/csv";
import { formatDateToLocale } from "../../../utils/dates";
import {
  ClassificationValue,
  formatHotspotName,
  getClassification
} from "../../../utils/hotspotUtils";
import { truncateText } from "../../../utils/text";
import { HotspotLocation, PlanType } from "../../../utils/types";
import useDebounce from "../../../utils/useDebounce";
import useFilteredLabels, {
  getFilteredLabels,
  LabelFilterKey
} from "../../../utils/useFilteredLabels";
import useMapConfig from "../../../utils/useMapConfig";
import useTransmitScaleConfig from "../../../utils/useTransmitScaleConfig";
import SuperSimulationSidebar from "../../Map/LocationDetails/SuperSimulationsTab/SuperSimulationSidebar";
import EntityDataTable from "../common/EntityDataTable";
import MemberCell from "../common/MemberCell";
import TablePropertiesSidebar from "../common/TablePropertiesSidebar";
import TableSearchInput from "../common/TableSearchInput";
import useDataTable, { Property } from "../common/useDataTable";
import { PaginationKey } from "../common/usePagination";
import { isActiveInstall } from "../InstallsData/utils";
import LockedWorkspace from "../LockedWorkspace";
import LockedWorkspaceByCommunityPlan from "../LockedWorkspaceByCommunityPlan";
import LockedWorkspaceByMembers from "../LockedWorkspaceByMembers";
import ImportLocationsModal from "./ImportLocationsModal";
import RunSuperSimulationsModal from "./RunSuperSimulationsModal";

interface Props {}

const LocationsData: React.FC<Props> = () => {
  const { setModalWarningConfig } = useToast();
  const { getPercentile } = useStats();
  const { wrapActionWithAuth } = useAuthentication();
  const renderDistance = useMapConfig((state) => state.renderDistance);
  const {
    currentWorkspace,
    currentWorkspaceId,
    workspaces,
    currentWorkspaceIsLocked,
    currentWorkspaceAllowedForCommunityPlan
  } = useWorkspaces();
  const { hotspotsMap } = useHotspots();
  const {
    locations,
    removeLocations,
    setEditLocationConfig,
    abortBulkLocationsRequest,
    fetchLocationDetails
  } = useLocations();
  const { installs, toggleInstallSidebar } = useInstalls();
  const { labels, linkedLabels } = useLabels();
  const filteredLabels = useFilteredLabels((state) => state.filteredLabels);
  const toggleIsActive = useTransmitScaleConfig(
    (state) => state.toggleIsActive
  );
  const [showPropertiesSidebar, setShowPropertiesSidebar] = useState(false);
  const sortOrder = useDataTable((state) => state.sortOrder[Entity.location]);
  const searchInput = useDataTable(
    (state) => state.searchInput[Entity.location]
  );
  const debouncedSearchInput = useDebounce(searchInput, 200);
  const [showImportLocationsModal, setShowImportLocationsModal] =
    useState(false);
  const [superSimulationsModalConfig, setSuperSimulationsModalConfig] =
    useState<{ locations: HotspotLocation[] } | undefined>(undefined);
  const [currentLocation, setCurrentLocation] = useState<
    HotspotLocation | undefined
  >();
  const [openSuperSimulationSidebar, setOpenSuperSimulationSidebar] =
    useState(false);
  const { calculatedTransmitScales, isTransmitScaleActive } =
    useTransmitScales();
  const { membersLimitExceeded } = useMembers();
  const { getIsReady } = useLoading();
  const { isDark } = useAppearance();
  const { getPlanByType, setNewPlan } = useBillingPlans();
  const [
    importLocationsModalErrorMessage,
    setImportLocationsModalErrorMessage
  ] = useState<string | null>(null);
  const [
    runSuperSimulationsModalErrorMessage,
    setRunSuperSimulationsModalErrorMessage
  ] = useState<string | null>(null);
  const { setModalUpgradeConfig } = useToast();
  const [upgradeSimulationsConfig, setUpgradeSimulationsConfig] =
    useState<ModalConfig>({
      open: false,
      resolve: () => {
        toggleSuperSimulationSidebar();
      }
    });
  const history = useHistory();

  const hasPendingSuperSimulations = useMemo(() => {
    const data: string[] = [];

    values(locations).forEach((location) => {
      const lastSuperSimulation = head(
        orderBy(
          location.super_simulations,
          [(superSimulation) => moment(superSimulation.created_at).valueOf()],
          ["desc"]
        )
      );

      if (
        lastSuperSimulation &&
        !lastSuperSimulation.simulation_report &&
        !data.includes(location.uuid)
      ) {
        data.push(location.uuid);
      }
    });

    return data;
  }, [locations]);

  useEffect(() => {
    let interval = setInterval(() => {
      hasPendingSuperSimulations.forEach((id: string) => {
        fetchLocationDetails(id);
      });
    }, 50000);

    return () => {
      clearInterval(interval);
    };
  }, [fetchLocationDetails, hasPendingSuperSimulations]);

  const getActiveInstall = useCallback(
    ({ uuid }: HotspotLocation) =>
      find(values(installs).filter(isActiveInstall), { location_uuid: uuid }),
    [installs]
  );

  const smartLabelFilters: {
    [key: string]: (location: HotspotLocation) => boolean;
  } = useMemo(
    () => ({
      "active install": (location) => !!getActiveInstall(location),
      "no active install": (location) => !getActiveInstall(location),
      "has super simulations": (location) =>
        !isEmpty(location.super_simulations),
      "no super simulations": (location) => isEmpty(location.super_simulations),
      activated: ({ uuid }) => isTransmitScaleActive(uuid),
      ...reduce(
        [
          "excellent",
          "good",
          "mediocre",
          "poor",
          "terrible",
          "inactive",
          "disabled"
        ],
        (
          acc: {
            [key: string]: (location: HotspotLocation) => boolean;
          },
          classification
        ) => {
          acc[`${classification} transmit scale`] = ({ uuid }) => {
            const isActive = isTransmitScaleActive(uuid);
            const locationClassification = isActive
              ? getClassification(get(calculatedTransmitScales, uuid, 0))
              : "disabled";
            return classification === locationClassification;
          };

          return acc;
        },
        {}
      )
    }),
    [calculatedTransmitScales, getActiveInstall, isTransmitScaleActive]
  );

  const filteredLocationLabels = useMemo(
    () =>
      getFilteredLabels(
        filteredLabels[LabelFilterKey.workspaceLocations],
        labels,
        smartLabelFilters,
        Entity.location
      ),
    [filteredLabels, labels, smartLabelFilters]
  );

  const filteredLocations = useMemo(() => {
    let filteredList = values(locations).filter((location) => {
      if (
        !isEmpty(filteredLocationLabels) &&
        !every(filteredLocationLabels, ({ uuid }) => {
          const smartFilterFunction = (smartLabelFilters as any)[uuid]; // TODO: fix typing

          return smartFilterFunction
            ? smartFilterFunction(location)
            : (linkedLabels[location.uuid] || []).includes(uuid);
        })
      ) {
        return false;
      }

      if (debouncedSearchInput) {
        const searchInputWords = words(debouncedSearchInput, /[^, ]+/g).map(
          toLower
        );

        return every(
          searchInputWords,
          (word) =>
            toLower(location.name).includes(word) ||
            toLower(location.address || "").includes(word)
        );
      }
      return true;
    });

    if (sortOrder) {
      type Iteratee = string | ((location: HotspotLocation) => number);
      let iteratee: Iteratee;

      switch (sortOrder.iteratee) {
        case "simulatedTransmitScale":
          iteratee = (location) =>
            get(calculatedTransmitScales, location.uuid, 0);
          break;
        case "lastSuperSimulation":
          iteratee = (location) => {
            const lastSuperSimulation = head(
              orderBy(
                location.super_simulations,
                [
                  (superSimulation) =>
                    moment(superSimulation.created_at).valueOf()
                ],
                ["desc"]
              )
            );

            return get(lastSuperSimulation, "simulation_report.ps", 0);
          };
          break;
        default:
          iteratee = sortOrder.iteratee;
      }

      filteredList = orderBy(filteredList, [iteratee], [sortOrder.order]);
    }

    if (currentWorkspace?.active_plan?.allowed_locations === 0) return [];

    return filteredList.slice(
      -(currentWorkspace?.active_plan?.allowed_locations || 0)
    );
  }, [
    locations,
    sortOrder,
    currentWorkspace,
    filteredLocationLabels,
    debouncedSearchInput,
    smartLabelFilters,
    linkedLabels,
    calculatedTransmitScales
  ]);

  const toggleSuperSimulationSidebar = useCallback(() => {
    if (openSuperSimulationSidebar) {
      setOpenSuperSimulationSidebar(false);
      return;
    }

    setOpenSuperSimulationSidebar(true);
  }, [openSuperSimulationSidebar]);

  const properties: { [key: string]: Property<HotspotLocation> } = useMemo(
    () => ({
      name: {
        key: "name",
        title: "Name",
        style: { minWidth: "35rem" },
        sortIteratee: "name",
        renderContent: (location) => (
          <Link
            className="relative flex items-center space-x-3 group text-sm font-medium text-gray-900 dark:text-white"
            to={`/locations/${location.uuid}/super-simulations`}
          >
            <div className="flex-shrink-0">
              <span className="inline-block relative">
                <LocationMarkerIcon className="h-6 w-6 text-gray-500 group-hover:text-gray-900 dark:text-gray-300 dark:group-hover:text-white" />
              </span>
            </div>
            <div className="min-w-0 focus:outline-none">
              <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:underline truncate">
                {location.name}
              </p>
            </div>
          </Link>
        )
      },
      addInstall: {
        key: "addInstall",
        title: "Install",
        excludeForExport: true,
        style: { minWidth: "19rem" },
        renderContent: (location) => {
          const activeInstall = getActiveInstall(location);

          if (!activeInstall) {
            return (
              <button
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-default dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400"
                onClick={() =>
                  toggleInstallSidebar({
                    uuid: "",
                    created_by: "",
                    created_at: "",
                    updated_by: "",
                    updated_at: "",
                    hotspot_uuid: "",
                    location_uuid: location.uuid,
                    installed_at: "",
                    uninstalled_at: "",
                    commissions: []
                  })
                }
              >
                Add install
              </button>
            );
          }

          const hotspot = hotspotsMap[activeInstall.hotspot_uuid];

          const renderActiveInstallLabel = (
            <Link to={`/installs/${activeInstall.uuid}/info`}>
              <div
                className="inline-flex items-center rounded-full font-medium px-3 text-sm"
                style={getLabelColorStyle("#e4e669", isDark)}
              >
                active install
              </div>
            </Link>
          );

          if (!hotspot) return renderActiveInstallLabel;

          return (
            <Link
              className="relative flex items-center space-x-3 group text-sm font-medium text-gray-900 dark:text-white"
              to={`/installs/${activeInstall.uuid}/info`}
            >
              <div className="flex-shrink-0">
                <span className="inline-block relative">
                  <img
                    className="h-8 w-8 rounded-full"
                    src={stringifyUrl({
                      url: `${avatarsApi}/bottts/${activeInstall.hotspot_uuid}.svg`,
                      query: { w: 40, h: 40 }
                    })}
                    alt="Hotspot avatar"
                  />
                </span>
              </div>
              <div className="flex-1 min-w-0 focus:outline-none">
                <span className="absolute inset-0" aria-hidden="true" />
                <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:underline">
                  {hotspot
                    ? formatHotspotName(hotspot)
                    : truncateText(activeInstall.hotspot_uuid, 20)}
                </p>
                {!hotspot.online && (
                  <div
                    className="inline-flex items-center rounded-full font-medium px-3 text-sm mt-1"
                    style={getLabelColorStyle("#f87171", isDark)}
                  >
                    inactive
                  </div>
                )}
              </div>
            </Link>
          );
        }
      },
      labels: {
        key: "labels",
        title: "Labels",
        style: { minWidth: "26rem" },
        renderContent: (location) => (
          <Labels
            entity={Entity.location}
            linkedUuid={location.uuid}
            filterKey={LabelFilterKey.workspaceLocations}
          />
        )
      },
      address: {
        key: "address",
        title: "Address",
        style: { minWidth: "30rem" },
        sortIteratee: "address",
        renderContent: (location) => location.address
      },
      coordinates: {
        key: "coordinates",
        title: "Coordinates",
        style: { minWidth: "10rem" },
        renderContent: (location) => (
          <Tooltip overlay="Open location in Google Earth" placement="left">
            <ExternalLink
              iconStyle={{ width: "1rem", height: "1rem" }}
              text={`${location.lat},${location.lng}`}
              url={`https://earth.google.com/web/search/${location.lat},${location.lng}/`}
            />
          </Tooltip>
        )
      },
      height: {
        key: "height",
        title: "Installation height",
        style: { minWidth: "14rem" },
        sortIteratee: "height",
        renderContent: (location) =>
          location.height ? renderDistance(location.height, false) : "-"
      },
      linkedContactsCount: {
        key: "linkedContactsCount",
        title: "Contacts",
        style: { minWidth: "8rem" },
        renderContent: (location) => location.linked_contact_ids.length
      },
      newSuperSimulation: {
        key: "newSuperSimulation",
        title: "New Super Simulation",
        style: { minWidth: "15rem" },
        renderContent: (location) => (
          <button
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-default dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400"
            onClick={() => {
              setCurrentLocation(location);
              toggleSuperSimulationSidebar();
            }}
          >
            Run Super Simulation
          </button>
        )
      },
      lastSuperSimulation: {
        key: "lastSuperSimulation",
        title: "Last Super Simulation",
        style: { minWidth: "15rem" },
        sortIteratee: "lastSuperSimulation",
        renderContent: (location) => {
          const lastSuperSimulation = head(
            orderBy(
              location.super_simulations,
              [
                (superSimulation) =>
                  moment(superSimulation.created_at).valueOf()
              ],
              ["desc"]
            )
          );

          if (!lastSuperSimulation) return null;

          if (!lastSuperSimulation.simulation_report) {
            return (
              <div className="flex items-center py-[5px]">
                <LoadingIcon className="animate-spin mr-2 h-4 w-4" />
                <span className="text-sm text-gray-500 font-medium dark:text-gray-50">
                  Processing
                </span>
              </div>
            );
          }

          const pocScore = lastSuperSimulation.simulation_report.ps;

          return (
            <Link
              className="relative flex items-center space-x-3 group text-sm font-medium text-gray-900 dark:text-white group"
              to={`/locations/${lastSuperSimulation.location_uuid}/super-simulations/${lastSuperSimulation.uuid}/visible-hotspots`}
            >
              <div className="flex-1 min-w-0 focus:outline-none">
                <span className="absolute inset-0" aria-hidden="true" />
                <div className="text-sm font-medium text-gray-900 dark:text-white group-hover:underline mb-1">
                  {pocScore ? (
                    <ClassificationValue
                      value={(getPercentile("poc_score", pocScore) || 0) / 100}
                      alternativeContent={
                        getPercentile("poc_score", pocScore) != null
                          ? `${capitalize(
                              renderPercentileClassification(
                                getPercentile("poc_score", pocScore)
                              )
                            )} PoC Score`
                          : "Loading..."
                      }
                      withoutTransmitScaleTooltip
                    />
                  ) : (
                    "No visible hotspots"
                  )}
                </div>
                <div className="flex items-center flex-wrap">
                  <div
                    className="inline-flex items-center rounded-full font-medium px-3 text-sm mr-2 mt-1"
                    style={getLabelColorStyle("#7057ff", isDark)}
                  >
                    {renderDistance(lastSuperSimulation.height, false)}
                  </div>
                  <div
                    className="inline-flex items-center rounded-full font-medium px-3 text-sm mt-1"
                    style={getLabelColorStyle("#d73aaa", isDark)}
                  >
                    {moment(lastSuperSimulation.created_at).fromNow()}
                  </div>
                </div>
              </div>
            </Link>
          );
        }
      },
      activated: {
        key: "activated",
        title: "Activated",
        renderContent: (location) => {
          if (!!getActiveInstall(location)) {
            return (
              <div className="flex items-center">
                <span>No</span>
                <Tooltip
                  overlay={
                    <>
                      Simulation is disabled for this location as there is
                      currently a hotspot installed in this location.{" "}
                      <Link
                        className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
                        to={`/installs/${
                          find(values(installs).filter(isActiveInstall), {
                            location_uuid: location.uuid
                          })?.uuid
                        }`}
                      >
                        Go to install
                      </Link>
                    </>
                  }
                >
                  <InfoIcon className="inline-flex h-4 w-4 ml-1" />
                </Tooltip>
              </div>
            );
          }

          const isActive = isTransmitScaleActive(location.uuid);

          return (
            <div className="flex items-center">
              <button
                type="button"
                className={classNames(
                  "relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none",
                  isActive
                    ? "bg-hotspotty-400 dark:bg-gray-100 dark:hover:bg-gray-200"
                    : "bg-gray-200 dark:bg-gray-500 dark:hover:bg-gray-400"
                )}
                aria-pressed="false"
                aria-labelledby="toggle-interactive"
                onClick={() => toggleIsActive(location.uuid)}
              >
                <span
                  aria-hidden="true"
                  className={classNames(
                    "pointer-events-none inline-block h-5 w-5 rounded-full bg-white dark:bg-gray-800 shadow transform ring-0 transition ease-in-out duration-200",
                    isActive ? "translate-x-5" : "translate-x-0"
                  )}
                />
              </button>
            </div>
          );
        }
      },
      simulatedTransmitScale: {
        key: "simulatedTransmitScale",
        title: "Simulated transmit scale",
        style: { minWidth: "16rem" },
        sortIteratee: "simulatedTransmitScale",
        renderContent: (location) => {
          const calculatedTransmitScale = get(
            calculatedTransmitScales,
            location.uuid,
            0
          );

          if (!calculatedTransmitScale) return null;

          return <ClassificationValue value={calculatedTransmitScale} />;
        }
      },
      created_at: {
        key: "created_at",
        title: "Added",
        style: { minWidth: "13rem" },
        sortIteratee: "created_at",
        renderContent: (location) => (
          <MemberCell
            memberUuid={location.created_by}
            subtitle={formatDateToLocale(location.created_at)}
          />
        )
      },
      updated_at: {
        key: "updated_at",
        title: "Updated",
        style: { minWidth: "13rem" },
        sortIteratee: "updated_at",
        renderContent: (location) =>
          location.updated_by ? (
            <MemberCell
              memberUuid={location.updated_by}
              subtitle={formatDateToLocale(location.updated_at)}
            />
          ) : (
            "-"
          )
      },
      actions: {
        key: "actions",
        title: "Actions",
        renderContent: (location) => (
          <LocationActionsButton location={location} />
        )
      }
    }),
    [
      calculatedTransmitScales,
      getActiveInstall,
      getPercentile,
      hotspotsMap,
      installs,
      isDark,
      isTransmitScaleActive,
      renderDistance,
      toggleInstallSidebar,
      toggleIsActive,
      toggleSuperSimulationSidebar
    ]
  );

  const exportLocations = useCallback(
    (locationsToExport: HotspotLocation[]) => {
      const name = `${
        workspaces[currentWorkspaceId as string].name
      } locations ${moment().utc().format("YYYY_MM_DD_HH_mm")}`;

      const enhancedLocationsToExport: ExportLocation[] = locationsToExport.map(
        ({ uuid, name, address, lat, lng, height = 0, super_simulations }) => ({
          uuid,
          name,
          address,
          lat,
          lng,
          height,
          superSimulationPoc:
            last(super_simulations)?.simulation_report?.ps || 0,
          superSimulationVisibleHotspots:
            last(super_simulations)?.simulation_report?.vh.length || 0,
          activated: isTransmitScaleActive(uuid) ? "yes" : "no",
          transmitScale: get(calculatedTransmitScales, uuid, 0),
          classification: isTransmitScaleActive(uuid)
            ? getClassification(get(calculatedTransmitScales, uuid, 0))
            : "disabled"
        })
      );

      downloadCsv(enhancedLocationsToExport, name);

      const locationsKmlContent = generateKML(name, enhancedLocationsToExport);

      downloadFile(locationsKmlContent, `${name}.kml`, "text/xml");
    },
    [
      calculatedTransmitScales,
      currentWorkspaceId,
      isTransmitScaleActive,
      workspaces
    ]
  );

  const isLoading = useMemo(
    () => isEmpty(locations) && !getIsReady([LoadingKey.workspaceLocations]),
    [getIsReady, locations]
  );

  const renderPageContent = useMemo(() => {
    if (currentWorkspaceIsLocked) {
      return (
        <LockedWorkspace
          title="Payment failed"
          paragraphs={[
            "Your subscription payment failed. Please resolve the issue with your payment method."
          ]}
          buttonText="Update payment details"
          buttonLink="/workspaces/settings/billing"
        />
      );
    }

    if (!currentWorkspaceAllowedForCommunityPlan) {
      return <LockedWorkspaceByCommunityPlan />;
    }

    if (membersLimitExceeded) {
      return <LockedWorkspaceByMembers />;
    }

    if (isLoading) {
      return <CenteredLoading className="sm:h-full h-96" />;
    }

    if (isEmpty(locations)) {
      return (
        <FeatureExplanation
          title="Locations"
          paragraphs={locationsExplanationParagraphs}
        >
          <button
            id="add-location-button"
            type="button"
            className="mt-6 inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
            onClick={wrapActionWithAuth(() => {
              setEditLocationConfig({ show: true });
            })}
          >
            Add location
          </button>
          <button
            type="button"
            className="mt-6 inline-flex items-center justify-center ml-3 px-3 py-1.5 border border-hotspotty-500 text-sm font-medium rounded-md shadow-sm text-hotspotty-500 hover:bg-hotspotty-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
            onClick={wrapActionWithAuth(() => {
              if (!currentWorkspace?.permissions["bulk-import-locations"]) {
                setModalUpgradeConfig({
                  open: true,
                  resolve: () => {
                    setNewPlan((prevState) => ({
                      ...prevState,
                      plan: getPlanByType(PlanType.business)
                    }));
                  },
                  nextPlan: getPlanByType(PlanType.business),
                  featureTitle: "Import Locations",
                  featureDescription:
                    "This feature is only available in workspaces with a business plan or higher. Upgrade your plan to unlock this feature."
                });
                return;
              }

              setShowImportLocationsModal(true);
            })}
          >
            Import locations
          </button>
        </FeatureExplanation>
      );
    }

    return (
      <>
        <div className="p-4">
          <TableSearchInput
            entity={Entity.location}
            placeholder="Search locations by name or address"
          />

          <div className="mt-2 flex items-center">
            <FilterLabels
              className="flex-1"
              entity={Entity.location}
              filteredLabels={filteredLocationLabels}
              smartLabels={keys(smartLabelFilters).map((key) =>
                getSmartLabelFromKey(key, Entity.location)
              )}
              filterKey={LabelFilterKey.workspaceLocations}
            />

            <DottedMenu
              text="Open options"
              menuItems={[
                {
                  disabled: isEmpty(filteredLocations),
                  onClick: () => {
                    setSuperSimulationsModalConfig({
                      locations: filteredLocations
                    });
                  },
                  text: `Run super simulation${
                    filteredLocations.length === 1 ? "" : "s"
                  }`
                },
                {
                  onClick: () => {
                    if (
                      !currentWorkspace?.permissions["bulk-import-locations"]
                    ) {
                      setModalUpgradeConfig({
                        open: true,
                        resolve: () => {
                          setNewPlan((prevState) => ({
                            ...prevState,
                            plan: getPlanByType(PlanType.business)
                          }));
                        },
                        nextPlan: getPlanByType(PlanType.business),
                        featureTitle: "Import Locations",
                        featureDescription:
                          "This feature is only available in workspaces with a business plan or higher. Upgrade your plan to unlock this feature."
                      });
                      return;
                    }

                    setShowImportLocationsModal(true);
                  },
                  text: "Import locations"
                },
                {
                  disabled: isEmpty(filteredLocations),
                  onClick: () => {
                    exportLocations(filteredLocations);
                  },
                  text: `Export ${
                    filteredLocationLabels.length ? "filtered" : "all"
                  } locations`
                },
                {
                  text: `Activate ${
                    filteredLocationLabels.length ? "filtered" : "all"
                  } locations`,
                  onClick: () => {
                    filteredLocations.forEach((location) => {
                      if (!isTransmitScaleActive(location.uuid)) {
                        toggleIsActive(location.uuid);
                      }
                    });
                  },
                  disabled: isEmpty(filteredLocations)
                },

                {
                  disabled: isEmpty(filteredLocations),
                  onClick: () => {
                    filteredLocations.forEach((location) => {
                      if (isTransmitScaleActive(location.uuid)) {
                        toggleIsActive(location.uuid);
                      }
                    });
                  },
                  text: `Deactivate ${
                    filteredLocationLabels.length ? "filtered" : "all"
                  } locations`
                },
                {
                  text: "Edit columns",
                  onClick: () => {
                    setShowPropertiesSidebar(true);
                  }
                },
                {
                  onClick: () => {
                    setModalWarningConfig({
                      open: true,
                      resolve: () => {
                        removeLocations(map(filteredLocations, "uuid"));

                        filteredLocations.forEach((location) => {
                          if (isTransmitScaleActive(location.uuid)) {
                            toggleIsActive(location.uuid);
                          }
                        });
                      }
                    });
                  },
                  text: `Remove ${
                    filteredLocationLabels.length ? "filtered" : "all"
                  } locations`,
                  disabled: isEmpty(filteredLocations)
                }
              ]}
            />

            <button
              id="add-location-button"
              type="button"
              className="ml-4 inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
              onClick={wrapActionWithAuth(() =>
                setEditLocationConfig({ show: true })
              )}
            >
              New location
            </button>
          </div>
        </div>

        <EntityDataTable<HotspotLocation>
          paginationKey={PaginationKey.workspaceLocations}
          entity={Entity.location}
          items={filteredLocations}
          properties={properties}
          selectionActions={[
            {
              title: "Run super simulations",
              callback: (selectedLocations: HotspotLocation[]) => {
                setSuperSimulationsModalConfig({
                  locations: selectedLocations
                });
              }
            },
            {
              title: "Export locations",
              callback: exportLocations
            },
            {
              title: "Activate locations",
              callback: (selectedLocations: HotspotLocation[]) => {
                selectedLocations.forEach((location) => {
                  if (!isTransmitScaleActive(location.uuid)) {
                    toggleIsActive(location.uuid);
                  }
                });
              }
            },
            {
              title: "Deactivate locations",
              callback: (selectedLocations: HotspotLocation[]) => {
                selectedLocations.forEach((location) => {
                  if (isTransmitScaleActive(location.uuid)) {
                    toggleIsActive(location.uuid);
                  }
                });
              }
            },
            {
              title: "Remove locations",
              callback: (
                selectedLocations: HotspotLocation[],
                setSelectedLocations: React.Dispatch<
                  React.SetStateAction<HotspotLocation[]>
                >
              ) => {
                setModalWarningConfig({
                  open: true,
                  resolve: () => {
                    removeLocations(map(selectedLocations, "uuid"));

                    selectedLocations.forEach((location) => {
                      if (isTransmitScaleActive(location.uuid)) {
                        toggleIsActive(location.uuid);
                      }
                    });

                    setSelectedLocations([]);
                  }
                });
              }
            }
          ]}
        />
      </>
    );
  }, [
    currentWorkspaceAllowedForCommunityPlan,
    currentWorkspace?.permissions,
    currentWorkspaceIsLocked,
    exportLocations,
    filteredLocationLabels,
    filteredLocations,
    getPlanByType,
    isLoading,
    isTransmitScaleActive,
    locations,
    membersLimitExceeded,
    properties,
    removeLocations,
    setEditLocationConfig,
    setModalUpgradeConfig,
    setModalWarningConfig,
    setNewPlan,
    smartLabelFilters,
    toggleIsActive,
    wrapActionWithAuth
  ]);

  return (
    <>
      {currentWorkspace &&
        currentWorkspace?.counts?.location >
          currentWorkspace.active_plan?.allowed_locations && (
          <NotificationBar
            linkTitle="Increase simulations limit"
            onClick={() => {
              if (currentWorkspace?.active_plan?.type !== PlanType.community) {
                setNewPlan((prevState) => ({
                  ...prevState,
                  simulationAmount: currentWorkspace?.counts.location || 0
                }));
                setUpgradeSimulationsConfig({ open: true });

                return;
              }

              history.push("/pricing");
            }}
            content={`This workspace has a limit of ${currentWorkspace?.active_plan?.allowed_locations} locations and your workspace has ${currentWorkspace?.counts?.location}.`}
          />
        )}

      <UpgradeSimulationsModal
        config={upgradeSimulationsConfig}
        setConfig={setUpgradeSimulationsConfig}
      />

      <TablePropertiesSidebar<HotspotLocation>
        entity={Entity.location}
        properties={properties}
        open={showPropertiesSidebar}
        setOpen={setShowPropertiesSidebar}
      />

      {importLocationsModalErrorMessage && (
        <AlertMessage
          className="mb-6"
          type="error"
          title="Import Locations"
          message={importLocationsModalErrorMessage}
          onClose={() => setImportLocationsModalErrorMessage(null)}
        />
      )}

      <ImportLocationsModal
        open={showImportLocationsModal}
        setErrorMessage={setImportLocationsModalErrorMessage}
        setOpen={(value) => {
          setShowImportLocationsModal(value);

          if (!value) {
            abortBulkLocationsRequest();
          }
        }}
      />

      {runSuperSimulationsModalErrorMessage && (
        <AlertMessage
          className="mb-6"
          type="error"
          title="Run Super Simulations"
          message={runSuperSimulationsModalErrorMessage}
          onClose={() => setRunSuperSimulationsModalErrorMessage(null)}
        />
      )}

      <RunSuperSimulationsModal
        setErrorMessage={setRunSuperSimulationsModalErrorMessage}
        onClose={() => setSuperSimulationsModalConfig(undefined)}
        config={superSimulationsModalConfig}
      />

      <SuperSimulationSidebar
        location={currentLocation}
        open={openSuperSimulationSidebar}
        toggle={toggleSuperSimulationSidebar}
        upgradeSimulationsConfig={upgradeSimulationsConfig}
        setUpgradeSimulationsConfig={setUpgradeSimulationsConfig}
      />

      {renderPageContent}
    </>
  );
};

export default LocationsData;

//
// Utils
//

interface ExportLocation {
  uuid: string;
  name: string;
  address: string | undefined | null;
  lat: number;
  lng: number;
  height: number;
  activated: "yes" | "no";
  transmitScale: number;
  classification: string;
  superSimulationPoc: number;
  superSimulationVisibleHotspots: number;
}

const generateKML = (
  name: string,
  exportLocations: ExportLocation[]
) => `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2" xmlns:gx="http://www.google.com/kml/ext/2.2" xmlns:kml="http://www.opengis.net/kml/2.2" xmlns:atom="http://www.w3.org/2005/Atom">
<Document id="1jIiwZJ0kILBl44eNlw8axXFep6CpL-uQ">
  <name>${name}</name>
	<StyleMap id="__managed_style_084446B4441BF51A2A6B">
		<Pair>
			<key>normal</key>
			<styleUrl>#__managed_style_1C11F604C01BF51A2A6B</styleUrl>
		</Pair>
		<Pair>
			<key>highlight</key>
			<styleUrl>#__managed_style_00263845E51BF51A2A6C</styleUrl>
		</Pair>
	</StyleMap>
	<gx:CascadingStyle kml:id="__managed_style_043A947E951BF51A8F55">
		<Style>
			<IconStyle>
				<Icon>
					<href>https://earth.google.com/earth/rpc/cc/icon?color=32c48c&amp;id=2000&amp;scale=4</href>
				</Icon>
				<hotSpot x="64" y="128" xunits="pixels" yunits="insetPixels"/>
			</IconStyle>
			<LabelStyle>
			</LabelStyle>
			<LineStyle>
				<color>ff2dc0fb</color>
				<width>2</width>
			</LineStyle>
			<PolyStyle>
				<color>40ffffff</color>
			</PolyStyle>
			<BalloonStyle>
				<displayMode>hide</displayMode>
			</BalloonStyle>
		</Style>
	</gx:CascadingStyle>
	<StyleMap id="__managed_style_0B96FB34A11BF51A8F54">
		<Pair>
			<key>normal</key>
			<styleUrl>#__managed_style_043A947E951BF51A8F55</styleUrl>
		</Pair>
		<Pair>
			<key>highlight</key>
			<styleUrl>#__managed_style_11225F7DE91BF51A8F55</styleUrl>
		</Pair>
	</StyleMap>
	<gx:CascadingStyle kml:id="__managed_style_22F8C9423D1BF51AF439">
		<Style>
			<IconStyle>
				<scale>1.2</scale>
				<Icon>
					<href>https://earth.google.com/earth/rpc/cc/icon?color=34d399&amp;id=2000&amp;scale=4</href>
				</Icon>
				<hotSpot x="64" y="128" xunits="pixels" yunits="insetPixels"/>
			</IconStyle>
			<LabelStyle>
			</LabelStyle>
			<LineStyle>
				<color>ff2dc0fb</color>
				<width>3</width>
			</LineStyle>
			<PolyStyle>
				<color>40ffffff</color>
			</PolyStyle>
			<BalloonStyle>
				<displayMode>hide</displayMode>
			</BalloonStyle>
		</Style>
	</gx:CascadingStyle>
	<gx:CascadingStyle kml:id="__managed_style_222194EAD71BF51BA0C3">
		<Style>
			<IconStyle>
				<scale>1.2</scale>
				<Icon>
					<href>https://earth.google.com/earth/rpc/cc/icon?color=fcc845&amp;id=2000&amp;scale=4</href>
				</Icon>
				<hotSpot x="64" y="128" xunits="pixels" yunits="insetPixels"/>
			</IconStyle>
			<LabelStyle>
			</LabelStyle>
			<LineStyle>
				<color>ff2dc0fb</color>
				<width>3</width>
			</LineStyle>
			<PolyStyle>
				<color>40ffffff</color>
			</PolyStyle>
			<BalloonStyle>
				<displayMode>hide</displayMode>
			</BalloonStyle>
		</Style>
	</gx:CascadingStyle>
	<gx:CascadingStyle kml:id="__managed_style_28EB7852A91BF51B4DA0">
		<Style>
			<IconStyle>
				<scale>1.2</scale>
				<Icon>
					<href>https://earth.google.com/earth/rpc/cc/icon?color=7c88bb&amp;id=2000&amp;scale=4</href>
				</Icon>
				<hotSpot x="64" y="128" xunits="pixels" yunits="insetPixels"/>
			</IconStyle>
			<LabelStyle>
			</LabelStyle>
			<LineStyle>
				<color>ff2dc0fb</color>
				<width>3</width>
			</LineStyle>
			<PolyStyle>
				<color>40ffffff</color>
			</PolyStyle>
			<BalloonStyle>
			</BalloonStyle>
		</Style>
	</gx:CascadingStyle>
	<gx:CascadingStyle kml:id="__managed_style_17C8B4CAB71BF51AF439">
		<Style>
			<IconStyle>
				<Icon>
					<href>https://earth.google.com/earth/rpc/cc/icon?color=34d399&amp;id=2000&amp;scale=4</href>
				</Icon>
				<hotSpot x="64" y="128" xunits="pixels" yunits="insetPixels"/>
			</IconStyle>
			<LabelStyle>
			</LabelStyle>
			<LineStyle>
				<color>ff2dc0fb</color>
				<width>2</width>
			</LineStyle>
			<PolyStyle>
				<color>40ffffff</color>
			</PolyStyle>
			<BalloonStyle>
				<displayMode>hide</displayMode>
			</BalloonStyle>
		</Style>
	</gx:CascadingStyle>
	<StyleMap id="__managed_style_059056D2011BF51BA0C3">
		<Pair>
			<key>normal</key>
			<styleUrl>#__managed_style_10D56093661BF51BA0C3</styleUrl>
		</Pair>
		<Pair>
			<key>highlight</key>
			<styleUrl>#__managed_style_222194EAD71BF51BA0C3</styleUrl>
		</Pair>
	</StyleMap>
	<StyleMap id="__managed_style_0A9AA7F0AE1BF51B4DA0">
		<Pair>
			<key>normal</key>
			<styleUrl>#__managed_style_1EC61444051BF51B4DA0</styleUrl>
		</Pair>
		<Pair>
			<key>highlight</key>
			<styleUrl>#__managed_style_28EB7852A91BF51B4DA0</styleUrl>
		</Pair>
	</StyleMap>
	<gx:CascadingStyle kml:id="__managed_style_1EC61444051BF51B4DA0">
		<Style>
			<IconStyle>
				<Icon>
					<href>https://earth.google.com/earth/rpc/cc/icon?color=7c88bb&amp;id=2000&amp;scale=4</href>
				</Icon>
				<hotSpot x="64" y="128" xunits="pixels" yunits="insetPixels"/>
			</IconStyle>
			<LabelStyle>
			</LabelStyle>
			<LineStyle>
				<color>ff2dc0fb</color>
				<width>2</width>
			</LineStyle>
			<PolyStyle>
				<color>40ffffff</color>
			</PolyStyle>
			<BalloonStyle>
			</BalloonStyle>
		</Style>
	</gx:CascadingStyle>
	<StyleMap id="__managed_style_06B6A9C0871BF51AF439">
		<Pair>
			<key>normal</key>
			<styleUrl>#__managed_style_17C8B4CAB71BF51AF439</styleUrl>
		</Pair>
		<Pair>
			<key>highlight</key>
			<styleUrl>#__managed_style_22F8C9423D1BF51AF439</styleUrl>
		</Pair>
	</StyleMap>
	<StyleMap id="__managed_style_0BD876C1B31BF51C8E5E">
		<Pair>
			<key>normal</key>
			<styleUrl>#__managed_style_1A34D24FDD1BF51C8E5E</styleUrl>
		</Pair>
		<Pair>
			<key>highlight</key>
			<styleUrl>#__managed_style_27665757551BF51C8E5E</styleUrl>
		</Pair>
	</StyleMap>
	<gx:CascadingStyle kml:id="__managed_style_11225F7DE91BF51A8F55">
		<Style>
			<IconStyle>
				<scale>1.2</scale>
				<Icon>
					<href>https://earth.google.com/earth/rpc/cc/icon?color=32c48c&amp;id=2000&amp;scale=4</href>
				</Icon>
				<hotSpot x="64" y="128" xunits="pixels" yunits="insetPixels"/>
			</IconStyle>
			<LabelStyle>
			</LabelStyle>
			<LineStyle>
				<color>ff2dc0fb</color>
				<width>3</width>
			</LineStyle>
			<PolyStyle>
				<color>40ffffff</color>
			</PolyStyle>
			<BalloonStyle>
				<displayMode>hide</displayMode>
			</BalloonStyle>
		</Style>
	</gx:CascadingStyle>
	<gx:CascadingStyle kml:id="__managed_style_2D8B89B7941BF51C2FF8">
		<Style>
			<IconStyle>
				<scale>1.2</scale>
				<Icon>
					<href>https://earth.google.com/earth/rpc/cc/icon?color=fea053&amp;id=2000&amp;scale=4</href>
				</Icon>
				<hotSpot x="64" y="128" xunits="pixels" yunits="insetPixels"/>
			</IconStyle>
			<LabelStyle>
			</LabelStyle>
			<LineStyle>
				<color>ff2dc0fb</color>
				<width>3</width>
			</LineStyle>
			<PolyStyle>
				<color>40ffffff</color>
			</PolyStyle>
			<BalloonStyle>
				<displayMode>hide</displayMode>
			</BalloonStyle>
		</Style>
	</gx:CascadingStyle>
	<gx:CascadingStyle kml:id="__managed_style_27665757551BF51C8E5E">
		<Style>
			<IconStyle>
				<scale>1.2</scale>
				<Icon>
					<href>https://earth.google.com/earth/rpc/cc/icon?color=e86161&amp;id=2000&amp;scale=4</href>
				</Icon>
				<hotSpot x="64" y="128" xunits="pixels" yunits="insetPixels"/>
			</IconStyle>
			<LabelStyle>
			</LabelStyle>
			<LineStyle>
				<color>ff2dc0fb</color>
				<width>3</width>
			</LineStyle>
			<PolyStyle>
				<color>40ffffff</color>
			</PolyStyle>
			<BalloonStyle>
			</BalloonStyle>
		</Style>
	</gx:CascadingStyle>
	<gx:CascadingStyle kml:id="__managed_style_1A34D24FDD1BF51C8E5E">
		<Style>
			<IconStyle>
				<Icon>
					<href>https://earth.google.com/earth/rpc/cc/icon?color=e86161&amp;id=2000&amp;scale=4</href>
				</Icon>
				<hotSpot x="64" y="128" xunits="pixels" yunits="insetPixels"/>
			</IconStyle>
			<LabelStyle>
			</LabelStyle>
			<LineStyle>
				<color>ff2dc0fb</color>
				<width>2</width>
			</LineStyle>
			<PolyStyle>
				<color>40ffffff</color>
			</PolyStyle>
			<BalloonStyle>
			</BalloonStyle>
		</Style>
	</gx:CascadingStyle>
	<gx:CascadingStyle kml:id="__managed_style_00263845E51BF51A2A6C">
		<Style>
			<IconStyle>
				<scale>1.2</scale>
				<Icon>
					<href>https://earth.google.com/earth/rpc/cc/icon?color=9ca3af&amp;id=2000&amp;scale=4</href>
				</Icon>
				<hotSpot x="64" y="128" xunits="pixels" yunits="insetPixels"/>
			</IconStyle>
			<LabelStyle>
			</LabelStyle>
			<LineStyle>
				<color>ff2dc0fb</color>
				<width>3</width>
			</LineStyle>
			<PolyStyle>
				<color>40ffffff</color>
			</PolyStyle>
			<BalloonStyle>
			</BalloonStyle>
		</Style>
	</gx:CascadingStyle>
	<gx:CascadingStyle kml:id="__managed_style_1C11F604C01BF51A2A6B">
		<Style>
			<IconStyle>
				<Icon>
					<href>https://earth.google.com/earth/rpc/cc/icon?color=9ca3af&amp;id=2000&amp;scale=4</href>
				</Icon>
				<hotSpot x="64" y="128" xunits="pixels" yunits="insetPixels"/>
			</IconStyle>
			<LabelStyle>
			</LabelStyle>
			<LineStyle>
				<color>ff2dc0fb</color>
				<width>2</width>
			</LineStyle>
			<PolyStyle>
				<color>40ffffff</color>
			</PolyStyle>
			<BalloonStyle>
			</BalloonStyle>
		</Style>
	</gx:CascadingStyle>
	<gx:CascadingStyle kml:id="__managed_style_17863AA4341BF51C2FF8">
		<Style>
			<IconStyle>
				<Icon>
					<href>https://earth.google.com/earth/rpc/cc/icon?color=fea053&amp;id=2000&amp;scale=4</href>
				</Icon>
				<hotSpot x="64" y="128" xunits="pixels" yunits="insetPixels"/>
			</IconStyle>
			<LabelStyle>
			</LabelStyle>
			<LineStyle>
				<color>ff2dc0fb</color>
				<width>2</width>
			</LineStyle>
			<PolyStyle>
				<color>40ffffff</color>
			</PolyStyle>
			<BalloonStyle>
				<displayMode>hide</displayMode>
			</BalloonStyle>
		</Style>
	</gx:CascadingStyle>
	<gx:CascadingStyle kml:id="__managed_style_10D56093661BF51BA0C3">
		<Style>
			<IconStyle>
				<Icon>
					<href>https://earth.google.com/earth/rpc/cc/icon?color=fcc845&amp;id=2000&amp;scale=4</href>
				</Icon>
				<hotSpot x="64" y="128" xunits="pixels" yunits="insetPixels"/>
			</IconStyle>
			<LabelStyle>
			</LabelStyle>
			<LineStyle>
				<color>ff2dc0fb</color>
				<width>2</width>
			</LineStyle>
			<PolyStyle>
				<color>40ffffff</color>
			</PolyStyle>
			<BalloonStyle>
				<displayMode>hide</displayMode>
			</BalloonStyle>
		</Style>
	</gx:CascadingStyle>
	<StyleMap id="__managed_style_0B59CCB5D31BF51C2FF8">
		<Pair>
			<key>normal</key>
			<styleUrl>#__managed_style_17863AA4341BF51C2FF8</styleUrl>
		</Pair>
		<Pair>
			<key>highlight</key>
			<styleUrl>#__managed_style_2D8B89B7941BF51C2FF8</styleUrl>
		</Pair>
	</StyleMap>
  ${exportLocations
    .map(
      ({ uuid, name, address, lat, lng, height, classification }) => `
  <Placemark id="${uuid}">
		<name>${name}</name>
		${address ? `<description>${address}</description>` : ""}
		<LookAt>
			<longitude>${lng}</longitude>
			<latitude>${lat}</latitude>
			<altitude>${height}</altitude>
			<heading>0</heading>
			<tilt>0</tilt>
			<gx:fovy>35</gx:fovy>
			<range>803.6065013544576</range>
			<altitudeMode>relativeToGround</altitudeMode>
		</LookAt>
		<styleUrl>#__managed_style_${
      classificationMarkerStyleIdMap[classification]
    }</styleUrl>
		<Point>
			<altitudeMode>relativeToGround</altitudeMode>
			<coordinates>${lng},${lat},0</coordinates>
		</Point>
	</Placemark>`
    )
    .join("\n")}
</Document>
</kml>`;

const classificationMarkerStyleIdMap: {
  [classification: string]: string;
} = {
  excellent: "0B96FB34A11BF51A8F54",
  good: "06B6A9C0871BF51AF439",
  mediocre: "059056D2011BF51BA0C3",
  poor: "0B59CCB5D31BF51C2FF8",
  terrible: "0BD876C1B31BF51C8E5E",
  inactive: "0A9AA7F0AE1BF51B4DA0",
  disabled: "084446B4441BF51A2A6B"
};

export const locationsExplanationParagraphs = [
  <>
    Locations represent physical locations that you have access to (optionally
    through your{" "}
    <Link
      className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
      to="/workspace/contacts"
    >
      contacts
    </Link>
    ).
  </>,
  <>
    So the address/coordinates/height above the ground of a location should
    represent where the{" "}
    <Link
      className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
      to="/workspace/hotspots"
    >
      hotspot
    </Link>{" "}
    can be installed physically.
  </>,
  <>
    Linking locations to{" "}
    <Link
      className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
      to="/workspace/contacts"
    >
      contacts
    </Link>{" "}
    makes it easier to manage payouts later on.
  </>
];
